import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Card, Row, Col, Table, Button, Spinner, Alert, Badge, Form, InputGroup, FormControl, Pagination, Tabs, Tab } from 'react-bootstrap';
import { adminAPI, paymentsAPI, invoicesAPI } from '../../services/apiService';
import { getTranslatedError } from '../../utils/errorTranslator';
import { debounce } from '../../utils/debounce';
import {
  buildTariffPreviewFromForm,
  getTariffLandingLabel,
  getTariffLandingPath,
  getTariffLandingUrl,
} from '../../utils/tariffLandingLink';
import { DollarOutlined, ReloadOutlined, PlusOutlined, EditOutlined, SearchOutlined, UserOutlined, HistoryOutlined, UnorderedListOutlined, FilterOutlined, DownloadOutlined, DeleteOutlined, CheckCircleOutlined, MessageOutlined } from '@ant-design/icons';
import { message, Select } from 'antd';
import { formatBalanceHistoryDescription } from '../../utils/balanceHistoryLabels';
import Avatar from '../common/Avatar';
import StackedModal from '../common/StackedModal';

const Modal = StackedModal;

const getDefaultTariffForm = () => ({
  name: '',
  price: '',
  requests_count: '',
  description: '',
  is_active: true,
  is_promotional: false,
  is_first_purchase_only: false,
  is_personal: false,
  eligibleUserIds: [],
  landing_type: '',
});

const formatUserOptionLabel = (user) => {
  const parts = [user.login, user.email, user.phone].filter(Boolean);
  return parts.join(' / ');
};

const formatSmsAmount = (value) => {
  const number = Number(value);
  if (Number.isNaN(number)) return '—';
  return number.toLocaleString('ru-RU');
};

const getSenderStatusVariant = (status) => {
  if (status === 'active' || status === 'default') return 'success';
  if (status === 'pending' || status === 'new') return 'warning';
  if (status === 'blocked') return 'danger';
  return 'secondary';
};

const BalanceManagement = () => {
  const [activeTab, setActiveTab] = useState('users');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Данные
  const [users, setUsers] = useState([]);
  const [balanceHistory, setBalanceHistory] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [usersList, setUsersList] = useState([]); // Список пользователей для фильтра
  
  // Пагинация и фильтры для пользователей
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // '' | 'active' | 'inactive'
  
  // Refs для хранения актуальных значений (чтобы избежать проблем с замыканиями)
  const searchRef = useRef(search);
  const statusFilterRef = useRef(statusFilter);
  const paginationRef = useRef(pagination);
  
  // Обновляем refs при изменении значений
  useEffect(() => {
    searchRef.current = search;
  }, [search]);
  
  useEffect(() => {
    statusFilterRef.current = statusFilter;
  }, [statusFilter]);
  
  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  // Фильтры и пагинация для истории
  const [historyPagination, setHistoryPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  });
  
  const [smsBoom, setSmsBoom] = useState(null);
  const [smsBoomError, setSmsBoomError] = useState('');
  const [smsBoomLoading, setSmsBoomLoading] = useState(false);

  const [historySubTab, setHistorySubTab] = useState('payments');
  const [historyFilters, setHistoryFilters] = useState({
    userId: '',
    operationType: '',
    sourceFilter: '',
    startDate: '',
    endDate: '',
  });
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [paymentSummary, setPaymentSummary] = useState({ totalAmount: 0, paymentCount: 0, currency: 'RUB' });
  const [paymentPagination, setPaymentPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  });
  const [paymentFilters, setPaymentFilters] = useState({
    userId: '',
    startDate: '',
    endDate: '',
    paidOnly: true,
  });
  const [exportLoading, setExportLoading] = useState(false);
  const [invoiceHistory, setInvoiceHistory] = useState([]);
  const [invoicePagination, setInvoicePagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  });
  const [invoiceFilters, setInvoiceFilters] = useState({
    userId: '',
    inn: '',
    organization: '',
    startDate: '',
    endDate: '',
  });
  const [invoiceDownloadLoading, setInvoiceDownloadLoading] = useState(null);
  const [invoiceDeleteLoading, setInvoiceDeleteLoading] = useState(null);
  const [invoiceCreditLoading, setInvoiceCreditLoading] = useState(null);
  const [unpaidInvoiceCount, setUnpaidInvoiceCount] = useState(0);

  // Модальные окна
  const [showAddBalanceModal, setShowAddBalanceModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showTariffModal, setShowTariffModal] = useState(false);
  const [editingTariff, setEditingTariff] = useState(null);
  const [showCreditResultModal, setShowCreditResultModal] = useState(false);
  const [creditResult, setCreditResult] = useState(null);
  const [creditLoading, setCreditLoading] = useState(false);
  
  // Формы
  const [balanceForm, setBalanceForm] = useState({
    amount: '',
    reason: '',
    operation: 'add', // 'add' or 'deduct'
  });

  const [tariffForm, setTariffForm] = useState(getDefaultTariffForm());
  const [tariffUserOptions, setTariffUserOptions] = useState([]);
  const [tariffUserSearchLoading, setTariffUserSearchLoading] = useState(false);

  const searchTariffUsers = useMemo(
    () => debounce(async (searchValue) => {
      try {
        setTariffUserSearchLoading(true);
        const data = await adminAPI.getUsersList({
          search: searchValue || undefined,
          limit: 50,
        });
        setTariffUserOptions(Array.isArray(data.users) ? data.users : []);
      } catch (err) {
        console.error('Ошибка поиска пользователей для тарифа:', err);
      } finally {
        setTariffUserSearchLoading(false);
      }
    }, 300),
    []
  );

  useEffect(() => {
    return () => {
      searchTariffUsers.cancel?.();
    };
  }, [searchTariffUsers]);

  // Загрузка списка всех пользователей для фильтра
  const loadUsersList = useCallback(async () => {
    try {
      // Запрашиваем без пагинации или с большим лимитом, чтобы получить всех
      // В идеале нужен отдельный эндпоинт для селекта, но используем список
      const data = await adminAPI.getUsers({ limit: 1000 });
      setUsersList(Array.isArray(data.users) ? data.users : []);
    } catch (err) {
      console.error('Ошибка загрузки списка пользователей:', err);
    }
  }, []);

  const normalizeBalanceHistoryResponse = (data) => {
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.history)) return data.history;
    if (Array.isArray(data)) return data;
    throw new Error('Неверный формат истории баланса: ожидается { items: [] } или { history: [] }.');
  };

  const loadUsers = async (pageOverride = null, limitOverride = null, searchOverride = null, statusFilterOverride = null) => {
    try {
      setLoading(true);
      setError('');
      
      // Определяем параметры для запроса
      // Используем переданные значения или актуальные из refs
      const currentPage = pageOverride !== null ? pageOverride : paginationRef.current.page;
      const currentLimit = limitOverride !== null ? limitOverride : paginationRef.current.limit;
      const currentSearch = searchOverride !== null ? searchOverride : searchRef.current;
      const currentStatusFilter = statusFilterOverride !== null ? statusFilterOverride : statusFilterRef.current;
      
      const params = {
        page: currentPage,
        limit: currentLimit,
        search: currentSearch,
        ...(currentStatusFilter && { isActive: currentStatusFilter === 'active' })
      };
      
      const data = await adminAPI.getUsers(params);
      
      // Получаем список пользователей
      const usersList = Array.isArray(data.users) ? data.users : [];
      
      // Вычисляем total и pages
      let total = 0;
      if (data.pagination) {
        total = data.pagination.total || 0;
      } else if (data.total !== undefined) {
        total = data.total || 0;
      }
      
      const pages = total > 0 ? Math.ceil(total / currentLimit) : 0;
      
      // Обновляем состояние атомарно: сначала users, потом pagination
      setUsers(usersList);
      
      // Обновляем пагинацию с правильными значениями
      setPagination({
        page: currentPage,
        limit: currentLimit,
        total: total,
        totalPages: pages,
      });
      
    } catch (err) {
      setError('Ошибка при загрузке пользователей: ' + getTranslatedError(err));
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: historyPagination.page,
        limit: historyPagination.limit,
        ...(historyFilters.userId && { userId: historyFilters.userId }),
        ...(historyFilters.operationType && { operationType: historyFilters.operationType }),
        ...(historyFilters.startDate && { startDate: historyFilters.startDate }),
        ...(historyFilters.endDate && { endDate: historyFilters.endDate }),
      };
      if (historyFilters.sourceFilter === 'payment') {
        params.referenceType = 'payment';
      } else if (historyFilters.sourceFilter === 'excludeManual') {
        params.excludeManual = true;
      }

      const data = await adminAPI.getBalanceHistory(params);
      const history = normalizeBalanceHistoryResponse(data);
      setBalanceHistory(history);
      if (data.pagination) {
        setHistoryPagination(prev => ({
          ...prev,
          total: data.pagination.total || 0,
          totalPages: data.pagination.pages || 0,
        }));
      } else if (typeof data.total === 'number' || typeof data.totalPages === 'number') {
        setHistoryPagination(prev => ({
          ...prev,
          total: data.total || prev.total,
          totalPages: data.totalPages || prev.totalPages,
        }));
      }
    } catch (err) {
      setError('Ошибка при загрузке истории: ' + getTranslatedError(err));
    } finally {
      setLoading(false);
    }
  }, [historyPagination.page, historyPagination.limit, historyFilters]);

  const loadPaymentHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        page: paymentPagination.page,
        limit: paymentPagination.limit,
        ...(paymentFilters.userId && { userId: paymentFilters.userId }),
        ...(paymentFilters.startDate && { startDate: paymentFilters.startDate }),
        ...(paymentFilters.endDate && { endDate: paymentFilters.endDate }),
        ...(paymentFilters.paidOnly && { status: 'succeeded' }),
      };

      const data = await paymentsAPI.getHistory(params);
      setPaymentHistory(data.payments || []);
      if (data.summary) {
        setPaymentSummary(data.summary);
      }
      if (data.pagination) {
        setPaymentPagination(prev => ({
          ...prev,
          total: data.pagination.total || 0,
          totalPages: data.pagination.pages || 0,
        }));
      }
    } catch (err) {
      setError('Ошибка при загрузке платежей: ' + getTranslatedError(err));
    } finally {
      setLoading(false);
    }
  }, [paymentPagination.page, paymentPagination.limit, paymentFilters]);

  const loadInvoiceHistory = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: invoicePagination.page,
        limit: invoicePagination.limit,
        ...(invoiceFilters.userId && { userId: invoiceFilters.userId }),
        ...(invoiceFilters.inn && { inn: invoiceFilters.inn }),
        ...(invoiceFilters.organization && { organization: invoiceFilters.organization }),
        ...(invoiceFilters.startDate && { startDate: invoiceFilters.startDate }),
        ...(invoiceFilters.endDate && { endDate: invoiceFilters.endDate }),
      };

      const data = await invoicesAPI.getAdminList(params);
      setInvoiceHistory(data.invoices || []);
      if (typeof data.unpaidCount === 'number') {
        setUnpaidInvoiceCount(data.unpaidCount);
      }
      if (data.pagination) {
        setInvoicePagination(prev => ({
          ...prev,
          total: data.pagination.total || 0,
          totalPages: data.pagination.pages || 0,
        }));
      }
    } catch (err) {
      setError('Ошибка при загрузке счетов: ' + getTranslatedError(err));
    } finally {
      setLoading(false);
    }
  }, [invoicePagination.page, invoicePagination.limit, invoiceFilters]);

  const handleHistoryFilterChange = (key, value) => {
    setHistoryFilters(prev => ({ ...prev, [key]: value }));
    setHistoryPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePaymentFilterChange = (key, value) => {
    setPaymentFilters(prev => ({ ...prev, [key]: value }));
    setPaymentPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleInvoiceFilterChange = (key, value) => {
    setInvoiceFilters(prev => ({ ...prev, [key]: value }));
    setInvoicePagination(prev => ({ ...prev, page: 1 }));
  };

  const handleDownloadInvoice = async (invoice) => {
    try {
      setInvoiceDownloadLoading(invoice.id);
      const response = await invoicesAPI.downloadInvoice(invoice.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${invoice.invoiceNumber || invoice.invoice_number || 'invoice'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      message.error('Ошибка скачивания счёта: ' + getTranslatedError(err));
    } finally {
      setInvoiceDownloadLoading(null);
    }
  };

  const handleCreditInvoice = async (invoice) => {
    const invoiceNumber = invoice.invoiceNumber || invoice.invoice_number || 'счёт';
    const login = invoice.user?.login || 'пользователя';
    const requests = invoice.requestsCount ?? invoice.requests_count ?? 0;
    if (!window.confirm(`Убедитесь, что пользователь оплатил счет № ${invoiceNumber}. Зачислить ${requests} фиксаций на баланс ${login}?`)) {
      return;
    }

    try {
      setInvoiceCreditLoading(invoice.id);
      const data = await invoicesAPI.creditInvoice(invoice.id);
      message.success('Оплата зачислена');
      setInvoiceHistory((prev) => prev.map((item) => (
        item.id === invoice.id
          ? { ...item, paidAt: data.invoice?.paidAt || new Date().toISOString() }
          : item
      )));
      setUnpaidInvoiceCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      message.error('Ошибка зачисления: ' + getTranslatedError(err));
    } finally {
      setInvoiceCreditLoading(null);
    }
  };

  const handleDeleteInvoice = async (invoice) => {
    const invoiceNumber = invoice.invoiceNumber || invoice.invoice_number || 'счёт';
    if (!window.confirm(`Удалить счёт № ${invoiceNumber}? PDF-файл также будет удалён.`)) {
      return;
    }

    try {
      setInvoiceDeleteLoading(invoice.id);
      await invoicesAPI.deleteInvoice(invoice.id);
      message.success('Счёт удалён');
      loadInvoiceHistory();
    } catch (err) {
      message.error('Ошибка удаления счёта: ' + getTranslatedError(err));
    } finally {
      setInvoiceDeleteLoading(null);
    }
  };

  const buildPaymentExportParams = () => ({
    ...(paymentFilters.userId && { userId: paymentFilters.userId }),
    ...(paymentFilters.startDate && { startDate: paymentFilters.startDate }),
    ...(paymentFilters.endDate && { endDate: paymentFilters.endDate }),
    ...(paymentFilters.paidOnly && { status: 'succeeded' }),
  });

  const handleExportPayments = async () => {
    try {
      setExportLoading(true);
      const response = await paymentsAPI.exportPaymentHistory(buildPaymentExportParams());
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const dateStamp = new Date().toISOString().slice(0, 10);
      link.href = url;
      link.download = `payments_${dateStamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      message.success('Файл Excel загружен');
    } catch (err) {
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          message.error(json.error || 'Ошибка экспорта');
          return;
        } catch {
          // fall through
        }
      }
      message.error('Ошибка экспорта: ' + getTranslatedError(err));
    } finally {
      setExportLoading(false);
    }
  };

  // Эффект для обновления истории токенов при изменении фильтров с debounce
  useEffect(() => {
    if (activeTab !== 'history' || historySubTab !== 'balance') return undefined;
    const timer = setTimeout(() => {
      loadHistory();
    }, 350);
    return () => clearTimeout(timer);
  }, [historyFilters, activeTab, historySubTab, loadHistory]);

  // Эффект для обновления платежей при изменении фильтров с debounce
  useEffect(() => {
    if (activeTab !== 'history' || historySubTab !== 'payments') return undefined;
    const timer = setTimeout(() => {
      loadPaymentHistory();
    }, 350);
    return () => clearTimeout(timer);
  }, [paymentFilters, activeTab, historySubTab, loadPaymentHistory]);

  useEffect(() => {
    if (activeTab !== 'history') return undefined;
    let cancelled = false;
    invoicesAPI.getAdminList({ page: 1, limit: 1 })
      .then((data) => {
        if (!cancelled && typeof data.unpaidCount === 'number') {
          setUnpaidInvoiceCount(data.unpaidCount);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'history' || historySubTab !== 'invoices') return undefined;
    const timer = setTimeout(() => {
      loadInvoiceHistory();
    }, 350);
    return () => clearTimeout(timer);
  }, [invoiceFilters, activeTab, historySubTab, loadInvoiceHistory]);

  useEffect(() => {
    if (activeTab !== 'history') return;
    if (historySubTab === 'payments') {
      loadPaymentHistory();
    } else if (historySubTab === 'invoices') {
      loadInvoiceHistory();
    } else {
      loadHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historySubTab]);

  useEffect(() => {
    if (activeTab === 'history' && historySubTab === 'balance') {
      loadHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyPagination.page, historyPagination.limit]);

  useEffect(() => {
    if (activeTab === 'history' && historySubTab === 'payments') {
      loadPaymentHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentPagination.page, paymentPagination.limit]);

  useEffect(() => {
    if (activeTab === 'history' && historySubTab === 'invoices') {
      loadInvoiceHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoicePagination.page, invoicePagination.limit]);

  const loadTariffs = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminAPI.getTariffs({ activeOnly: false });
      if (Array.isArray(data)) setTariffs(data);
      else if (data.tariffs) setTariffs(data.tariffs);
    } catch (err) {
      setError('Ошибка при загрузке тарифов: ' + getTranslatedError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSmsBoom = useCallback(async () => {
    try {
      setSmsBoomLoading(true);
      setSmsBoomError('');
      const data = await adminAPI.getSmsBoomAccount();
      setSmsBoom(data);
    } catch (err) {
      setSmsBoom(null);
      setSmsBoomError(getTranslatedError(err) || 'Не удалось загрузить данные SMS Boom');
    } finally {
      setSmsBoomLoading(false);
    }
  }, []);

  // Debounced поиск
  const debouncedLoadUsers = useMemo(
    () => debounce((searchValue, statusValue) => {
      loadUsers(1, null, searchValue, statusValue);
    }, 500),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (activeTab === 'users') {
      setPagination(prev => ({ ...prev, page: 1 }));
      debouncedLoadUsers(search, statusFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter, activeTab]);

  // Загрузка данных при смене вкладки
  useEffect(() => {
    if (activeTab === 'users' && !search && !statusFilter) {
      loadUsers();
    } else if (activeTab === 'history') {
      loadUsersList();
      if (historySubTab === 'payments') {
        loadPaymentHistory();
      } else if (historySubTab === 'invoices') {
        loadInvoiceHistory();
      } else {
        loadHistory();
      }
    } else if (activeTab === 'tariffs') {
      loadTariffs();
    } else if (activeTab === 'sms-boom') {
      loadSmsBoom();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleAddBalanceClick = (user) => {
    setSelectedUser(user);
    setBalanceForm({ amount: '', reason: '', operation: 'add' });
    setShowAddBalanceModal(true);
  };

  const handleAddBalanceSubmit = async (e) => {
    e.preventDefault();
    let amount = parseInt(balanceForm.amount, 10);
    if (isNaN(amount) || amount <= 0) {
      message.error('Введите положительное число');
      return;
    }

    if (balanceForm.operation === 'deduct') {
      const currentBalance = selectedUser.requestsBalance || selectedUser.requests_balance || 0;
      if (amount > currentBalance) {
        message.error('Нельзя списать больше, чем есть на балансе');
        return;
      }
      amount = -amount;
    }

    try {
      await adminAPI.updateUserBalance(selectedUser.id, {
        amount,
        reason: balanceForm.reason || (amount > 0 ? 'Ручное пополнение администратором' : 'Ручное списание администратором'),
      });
      message.success('Баланс успешно обновлен');
      setShowAddBalanceModal(false);
      loadUsers(); // Обновляем список пользователей
    } catch (err) {
      message.error('Ошибка при обновлении баланса: ' + getTranslatedError(err));
    }
  };

  const handleSaveTariff = async () => {
    try {
      if (!tariffForm.name || !tariffForm.price || !tariffForm.requests_count) {
        message.error('Заполните все обязательные поля');
        return;
      }

      if (tariffForm.is_personal && tariffForm.eligibleUserIds.length === 0) {
        message.error('Для персонального тарифа выберите хотя бы одного пользователя');
        return;
      }
      
      const dataToSend = {
        name: tariffForm.name,
        price: parseFloat(tariffForm.price),
        requests_count: parseInt(tariffForm.requests_count, 10),
        description: tariffForm.description || '',
        is_active: tariffForm.is_active,
        is_promotional: tariffForm.is_promotional,
        is_first_purchase_only: tariffForm.is_first_purchase_only,
        is_personal: tariffForm.is_personal,
        eligibleUserIds: tariffForm.is_personal ? tariffForm.eligibleUserIds : [],
        landing_type: tariffForm.landing_type === '' ? null : parseInt(tariffForm.landing_type, 10),
      };

      if (editingTariff) {
        await adminAPI.updateTariff(editingTariff.id, dataToSend);
        message.success('Тариф обновлен');
      } else {
        await adminAPI.createTariff(dataToSend);
        message.success('Тариф создан');
      }

      setShowTariffModal(false);
      setEditingTariff(null);
      setTariffForm(getDefaultTariffForm());
      setTariffUserOptions([]);
      loadTariffs();
    } catch (err) {
      message.error('Ошибка: ' + getTranslatedError(err));
    }
  };

  const handleEditTariff = (tariff) => {
    const eligibleUsers = tariff.eligibleUsers || [];
    setEditingTariff(tariff);
    setTariffForm({
      name: tariff.name || '',
      price: tariff.price || '',
      requests_count: tariff.requests_count || tariff.requestsCount || '',
      description: tariff.description || '',
      is_active: tariff.is_active !== undefined ? tariff.is_active : true,
      is_promotional: Boolean(tariff.is_promotional),
      is_first_purchase_only: Boolean(tariff.is_first_purchase_only),
      is_personal: Boolean(tariff.is_personal),
      eligibleUserIds: eligibleUsers.map((user) => user.id),
      landing_type: tariff.landing_type ?? '',
    });
    setTariffUserOptions(eligibleUsers);
    setShowTariffModal(true);
  };

  const handleCopyTariffLink = async (tariff) => {
    const link = getTariffLandingUrl(tariff);
    try {
      await navigator.clipboard.writeText(link);
      message.success('Ссылка для перехода с сайта скопирована');
    } catch (err) {
      message.info(link);
    }
  };

  const renderTariffTypeBadges = (tariff) => (
    <div className="d-flex flex-wrap gap-1">
      {tariff.is_promotional && <Badge bg="warning" text="dark">Акция</Badge>}
      {tariff.is_first_purchase_only && <Badge bg="info">Первая покупка</Badge>}
      {tariff.is_personal && <Badge bg="primary">Персональный</Badge>}
      {!tariff.is_promotional && !tariff.is_first_purchase_only && !tariff.is_personal && (
        <Badge bg="light" text="dark">Обычный</Badge>
      )}
    </div>
  );

  const handleDeleteTariff = async (tariff) => {
    if (!window.confirm(`Удалить тариф "${tariff.name}"? Это действие нельзя будет отменить.`)) {
      return;
    }
    try {
      await adminAPI.deleteTariff(tariff.id);
      message.success('Тариф удалён');
      loadTariffs();
    } catch (err) {
      message.error('Ошибка при удалении тарифа: ' + getTranslatedError(err));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleString('ru-RU');
    } catch {
      return dateString;
    }
  };

  const handleCreditMissingBalance = async () => {
    try {
      setCreditLoading(true);
      setError('');
      const data = await adminAPI.creditMissingBalance();
      setCreditResult(data);
      setShowCreditResultModal(true);
      if (data.credited > 0 || data.failed > 0) {
        loadUsers();
      }
    } catch (err) {
      message.error('Ошибка: ' + getTranslatedError(err));
    } finally {
      setCreditLoading(false);
    }
  };

  const getOperationBadge = (type) => {
    const types = {
      deposit: 'success',
      withdrawal: 'danger',
      payment: 'warning',
      refund: 'info',
      adjustment: 'secondary',
      generation: 'warning',
      generation_screenshot: 'warning',
    };
    
    const labels = {
      deposit: 'Пополнение',
      withdrawal: 'Вывод',
      payment: 'Списание',
      refund: 'Возврат',
      adjustment: 'Корректировка',
      generation: 'Генерация',
      generation_screenshot: 'Генерация (скриншот)',
    };

    return (
      <Badge bg={types[type] || 'secondary'}>
        {labels[type] || type || 'Неизвестно'}
      </Badge>
    );
  };

  const getPaymentStatusBadge = (status) => {
    const badges = {
      succeeded: { bg: 'success', text: 'Оплачен' },
      pending: { bg: 'warning', text: 'Ожидает' },
      waiting_for_capture: { bg: 'warning', text: 'Ожидает подтверждения' },
      canceled: { bg: 'secondary', text: 'Отменён' },
      refunded: { bg: 'info', text: 'Возврат' },
    };
    const badge = badges[status] || { bg: 'secondary', text: status };
    return <Badge bg={badge.bg}>{badge.text}</Badge>;
  };

  const formatMoney = (amount, currency = 'RUB') => {
    if (amount == null || Number.isNaN(amount)) return '—';
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: currency || 'RUB',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const renderHistoryPagination = (paginationState, setPaginationState) => {
    if (paginationState.totalPages <= 1) return null;
    return (
      <div className="d-flex justify-content-center mt-3">
        <Pagination>
          <Pagination.First
            disabled={paginationState.page === 1}
            onClick={() => setPaginationState({ ...paginationState, page: 1 })}
          />
          <Pagination.Prev
            disabled={paginationState.page === 1}
            onClick={() => setPaginationState({ ...paginationState, page: paginationState.page - 1 })}
          />
          {[...Array(paginationState.totalPages)].map((_, i) => {
            const page = i + 1;
            if (
              page === 1 ||
              page === paginationState.totalPages ||
              (page >= paginationState.page - 2 && page <= paginationState.page + 2)
            ) {
              return (
                <Pagination.Item
                  key={page}
                  active={page === paginationState.page}
                  onClick={() => setPaginationState({ ...paginationState, page })}
                >
                  {page}
                </Pagination.Item>
              );
            }
            return null;
          })}
          <Pagination.Next
            disabled={paginationState.page === paginationState.totalPages}
            onClick={() => setPaginationState({ ...paginationState, page: paginationState.page + 1 })}
          />
          <Pagination.Last
            disabled={paginationState.page === paginationState.totalPages}
            onClick={() => setPaginationState({ ...paginationState, page: paginationState.totalPages })}
          />
        </Pagination>
      </div>
    );
  };

  return (
    <div>
      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h5 className="mb-0">
              <DollarOutlined className="me-2" />
              Управление балансами
            </h5>
          </div>

          {error && (
            <Alert variant="danger" onClose={() => setError('')} dismissible>
              {error}
            </Alert>
          )}

          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-3"
          >
            <Tab 
              eventKey="users" 
              title={
                <span>
                  <UserOutlined className="me-2" />
                  Балансы пользователей
                </span>
              }
            >
              <Row className="mb-3">
                <Col md={3}>
                  <InputGroup>
                    <InputGroup.Text>
                      <SearchOutlined />
                    </InputGroup.Text>
                    <FormControl
                      placeholder="Поиск по логину или email..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </InputGroup>
                </Col>
                <Col md={2}>
                  <Form.Select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setPagination(prev => ({ ...prev, page: 1 }));
                    }}
                  >
                    <option value="">Все статусы</option>
                    <option value="active">Активные</option>
                    <option value="inactive">Неактивные</option>
                  </Form.Select>
                </Col>
                <Col md={2}>
                  <Form.Select
                    value={pagination.limit}
                    onChange={(e) => {
                      const newLimit = parseInt(e.target.value, 10);
                      loadUsers(1, newLimit);
                    }}
                  >
                    <option value={10}>10 на странице</option>
                    <option value={20}>20 на странице</option>
                    <option value={50}>50 на странице</option>
                    <option value={100}>100 на странице</option>
                  </Form.Select>
                </Col>
                <Col md={5} className="text-end">
                  <Button
                    variant="outline-warning"
                    className="me-2"
                    onClick={handleCreditMissingBalance}
                    disabled={creditLoading || loading}
                  >
                    {creditLoading ? <Spinner animation="border" size="sm" className="me-1" /> : null}
                    Пересчитать зависшие балансы
                  </Button>
                  <Button variant="outline-primary" onClick={() => loadUsers()} disabled={loading}>
                    <ReloadOutlined /> Обновить
                  </Button>
                </Col>
              </Row>

              {loading && activeTab === 'users' ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <Table striped bordered hover>
                      <thead>
                        <tr>
                          <th style={{ width: '50px' }}>Аватар</th>
                          <th>Пользователь</th>
                          <th>Email</th>
                          <th>Баланс (запросов)</th>
                          <th style={{ width: '150px' }}>Действия</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((user) => (
                          <tr key={user.id}>
                            <td>
                              <Avatar user={user} size={32} showBorder />
                            </td>
                            <td>
                              <div className="d-flex flex-column">
                                <span className="fw-bold">{user.login}</span>
                                <small className="text-muted">
                                  {(user.first_name || user.firstName) ? `${user.first_name || user.firstName} ${user.last_name || user.lastName || ''}` : ''}
                                </small>
                              </div>
                            </td>
                            <td>{user.email || '—'}</td>
                            <td>
                              <Badge 
                                bg={
                                  (user.requestsBalance || user.requests_balance || 0) > 10 ? 'success' : 
                                  (user.requestsBalance || user.requests_balance || 0) > 0 ? 'warning' : 'danger'
                                }
                                style={{ fontSize: '14px' }}
                              >
                                {user.requestsBalance || user.requests_balance || 0}
                              </Badge>
                            </td>
                            <td>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleAddBalanceClick(user)}
                              >
                                <EditOutlined /> Изменить
                              </Button>
                            </td>
                          </tr>
                        ))}
                        {users.length === 0 && (
                          <tr>
                            <td colSpan="5" className="text-center py-4 text-muted">
                              Пользователи не найдены
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>

                  {pagination.totalPages > 0 && (
                    <div className="d-flex justify-content-center mt-3">
                      <Pagination>
                        <Pagination.First
                          disabled={pagination.page === 1}
                          onClick={() => loadUsers(1)}
                        />
                        <Pagination.Prev
                          disabled={pagination.page === 1}
                          onClick={() => loadUsers(pagination.page - 1)}
                        />
                        {[...Array(pagination.totalPages)].map((_, i) => {
                          const page = i + 1;
                          if (
                            page === 1 ||
                            page === pagination.totalPages ||
                            (page >= pagination.page - 2 && page <= pagination.page + 2)
                          ) {
                            return (
                              <Pagination.Item
                                key={page}
                                active={page === pagination.page}
                                onClick={() => loadUsers(page)}
                              >
                                {page}
                              </Pagination.Item>
                            );
                          }
                          return null;
                        })}
                        <Pagination.Next
                          disabled={pagination.page === pagination.totalPages}
                          onClick={() => loadUsers(pagination.page + 1)}
                        />
                        <Pagination.Last
                          disabled={pagination.page === pagination.totalPages}
                          onClick={() => loadUsers(pagination.totalPages)}
                        />
                      </Pagination>
                    </div>
                  )}

                  {pagination.total > 0 && (
                    <div className="text-center text-muted mt-2" style={{ fontSize: '12px' }}>
                      Показано {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} из {pagination.total}
                    </div>
                  )}
                </>
              )}
            </Tab>

            <Tab 
              eventKey="tariffs" 
              title={
                <span>
                  <UnorderedListOutlined className="me-2" />
                  Тарифы
                </span>
              }
            >
              <div className="d-flex justify-content-end mb-3">
                <Button
                  variant="primary"
                  onClick={() => {
                    setEditingTariff(null);
                    setTariffForm(getDefaultTariffForm());
                    setTariffUserOptions([]);
                    setShowTariffModal(true);
                  }}
                >
                  <PlusOutlined /> Создать тариф
                </Button>
              </div>
              
              {loading && activeTab === 'tariffs' ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                </div>
              ) : (
                <div className="table-responsive">
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th>Название</th>
                        <th>Тип</th>
                        <th>Запросов</th>
                        <th>Цена</th>
                        <th>Переход с сайта</th>
                        <th>Статус</th>
                        <th>Действия</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tariffs.map((tariff) => (
                        <tr key={tariff.id}>
                          <td>{tariff.name}</td>
                          <td>{renderTariffTypeBadges(tariff)}</td>
                          <td>{tariff.requests_count || tariff.requestsCount || 0}</td>
                          <td><strong>{tariff.price} ₽</strong></td>
                          <td>
                            <code className="small">{getTariffLandingPath(tariff)}</code>
                            <div className="text-muted small">
                              {getTariffLandingLabel(tariff)}
                            </div>
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0"
                              onClick={() => handleCopyTariffLink(tariff)}
                            >
                              Копировать
                            </Button>
                          </td>
                          <td>
                            <Badge bg={tariff.is_active ? 'success' : 'secondary'}>
                              {tariff.is_active ? 'Активен' : 'Выключен'}
                            </Badge>
                          </td>
                          <td>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleEditTariff(tariff)}
                            >
                              <EditOutlined />
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              className="ms-2"
                              onClick={() => handleDeleteTariff(tariff)}
                            >
                              Удалить
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {tariffs.length === 0 && (
                        <tr>
                          <td colSpan="7" className="text-center py-4 text-muted">
                            Тарифы не найдены
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              )}
            </Tab>

            <Tab
              eventKey="sms-boom"
              title={
                <span>
                  <MessageOutlined className="me-2" />
                  SMS Boom
                </span>
              }
            >
              <div className="d-flex justify-content-end mb-3">
                <Button variant="outline-primary" size="sm" onClick={loadSmsBoom} disabled={smsBoomLoading}>
                  <ReloadOutlined className="me-1" />
                  Обновить
                </Button>
              </div>

              {smsBoomError && (
                <Alert variant="danger" className="mb-3">
                  {smsBoomError}
                </Alert>
              )}

              {smsBoomLoading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" />
                </div>
              ) : smsBoom ? (
                <>
                  <Row className="g-3 mb-3">
                    <Col md={3}>
                      <Card>
                        <Card.Body>
                          <div className="text-muted small">Баланс</div>
                          <div className="fs-4 fw-semibold">{formatSmsAmount(smsBoom.balance?.balance)}</div>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3}>
                      <Card>
                        <Card.Body>
                          <div className="text-muted small">Кредитный лимит</div>
                          <div className="fs-4 fw-semibold">{formatSmsAmount(smsBoom.balance?.creditLimit)}</div>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3}>
                      <Card>
                        <Card.Body>
                          <div className="text-muted small">Резерв</div>
                          <div className="fs-4 fw-semibold">{formatSmsAmount(smsBoom.balance?.reservedBalance)}</div>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3}>
                      <Card>
                        <Card.Body>
                          <div className="text-muted small">Доступный остаток</div>
                          <div className="fs-4 fw-semibold">{formatSmsAmount(smsBoom.balance?.availableBalance)}</div>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>

                  <Card className="mb-3">
                    <Card.Body>
                      <Row>
                        <Col md={3}>
                          <div className="text-muted small">Клиент</div>
                          <div>{smsBoom.account?.name || '—'}</div>
                        </Col>
                        <Col md={3}>
                          <div className="text-muted small">Статус</div>
                          <div>{smsBoom.account?.status || '—'}</div>
                        </Col>
                        <Col md={3}>
                          <div className="text-muted small">API</div>
                          <div>{smsBoom.account?.apiEnabled ? 'включён' : 'выключен'}</div>
                        </Col>
                        <Col md={3}>
                          <div className="text-muted small">Префикс токена</div>
                          <div>{smsBoom.account?.tokenPrefix || '—'}</div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  <Table striped bordered hover size="sm">
                    <thead>
                      <tr>
                        <th>Имя</th>
                        <th>Статус</th>
                        <th>Активна</th>
                        <th>По умолчанию</th>
                        <th>Примечание</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(smsBoom.senders || []).map((sender) => (
                        <tr key={sender.name}>
                          <td>{sender.name}</td>
                          <td>
                            <Badge bg={getSenderStatusVariant(sender.status)}>
                              {sender.status || '—'}
                            </Badge>
                          </td>
                          <td>{sender.isActive ? 'да' : 'нет'}</td>
                          <td>{sender.isDefault ? 'да' : 'нет'}</td>
                          <td>{sender.note || '—'}</td>
                        </tr>
                      ))}
                      {(smsBoom.senders || []).length === 0 && (
                        <tr>
                          <td colSpan="5" className="text-center py-4 text-muted">
                            Имена отправителей не найдены
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </>
              ) : null}
            </Tab>

            <Tab 
              eventKey="history" 
              title={
                <span>
                  <HistoryOutlined className="me-2" />
                  История операций
                </span>
              }
            >
              <Tabs
                activeKey={historySubTab}
                onSelect={(k) => k && setHistorySubTab(k)}
                className="mb-3"
              >
                <Tab eventKey="payments" title="Платежи (₽)">
                  <Alert variant="light" className="py-2 mb-3">
                    <strong>Итого оплачено:</strong>{' '}
                    {formatMoney(paymentSummary.totalAmount, paymentSummary.currency)}
                    {' '}
                    <span className="text-muted">
                      ({paymentSummary.paymentCount} {paymentSummary.paymentCount === 1 ? 'платёж' : 'платежей'})
                    </span>
                  </Alert>

                  <div className="mb-3">
                    <Row className="g-2 align-items-center">
                      <Col md={3}>
                        <Form.Select
                          value={paymentFilters.userId}
                          onChange={(e) => handlePaymentFilterChange('userId', e.target.value)}
                        >
                          <option value="">Все пользователи</option>
                          {usersList.map(u => (
                            <option key={u.id} value={u.id}>
                              {u.login} ({u.email || 'без email'})
                            </option>
                          ))}
                        </Form.Select>
                      </Col>
                      <Col md={2}>
                        <Form.Control
                          type="date"
                          value={paymentFilters.startDate}
                          onChange={(e) => handlePaymentFilterChange('startDate', e.target.value)}
                        />
                      </Col>
                      <Col md={2}>
                        <Form.Control
                          type="date"
                          value={paymentFilters.endDate}
                          onChange={(e) => handlePaymentFilterChange('endDate', e.target.value)}
                        />
                      </Col>
                      <Col md={2}>
                        <Form.Check
                          type="checkbox"
                          id="paidOnlyFilter"
                          label="Только оплаченные"
                          checked={paymentFilters.paidOnly}
                          onChange={(e) => handlePaymentFilterChange('paidOnly', e.target.checked)}
                        />
                      </Col>
                      <Col md={3} className="text-end">
                        <Button
                          variant="outline-secondary"
                          className="me-2"
                          onClick={() => {
                            setPaymentFilters({
                              userId: '',
                              startDate: '',
                              endDate: '',
                              paidOnly: true,
                            });
                          }}
                        >
                          <FilterOutlined /> Сброс
                        </Button>
                        <Button
                          variant="outline-success"
                          className="me-2"
                          onClick={handleExportPayments}
                          disabled={exportLoading}
                        >
                          {exportLoading ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            <>
                              <DownloadOutlined /> Excel
                            </>
                          )}
                        </Button>
                        <Button variant="outline-primary" onClick={loadPaymentHistory} disabled={loading}>
                          <ReloadOutlined /> Обновить
                        </Button>
                      </Col>
                    </Row>
                  </div>

                  {loading && activeTab === 'history' && historySubTab === 'payments' ? (
                    <div className="text-center py-5">
                      <Spinner animation="border" variant="primary" />
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <Table striped bordered hover size="sm">
                          <thead>
                            <tr>
                              <th>Пользователь</th>
                              <th>Пакет</th>
                              <th>Сумма</th>
                              <th>Генерации</th>
                              <th>Статус</th>
                              <th>Дата оплаты</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paymentHistory.map((item) => (
                              <tr key={item.id}>
                                <td>{item.user?.login || '—'}</td>
                                <td>{item.tariff?.name || item.description || '—'}</td>
                                <td>{formatMoney(item.amount, item.currency)}</td>
                                <td>{item.requestsCount ?? '—'}</td>
                                <td>{getPaymentStatusBadge(item.status)}</td>
                                <td>{formatDate(item.paidAt || item.createdAt)}</td>
                              </tr>
                            ))}
                            {paymentHistory.length === 0 && (
                              <tr>
                                <td colSpan="6" className="text-center py-4 text-muted">
                                  Платежи не найдены
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                      {renderHistoryPagination(paymentPagination, setPaymentPagination)}
                    </>
                  )}
                </Tab>

                <Tab
                  eventKey="invoices"
                  title={(
                    <span>
                      Счета
                      {unpaidInvoiceCount > 0 && (
                        <Badge bg="danger" className="ms-2">{unpaidInvoiceCount}</Badge>
                      )}
                    </span>
                  )}
                >
                  <div className="mb-3">
                    <Row className="g-2 align-items-center">
                      <Col md={3}>
                        <Form.Select
                          value={invoiceFilters.userId}
                          onChange={(e) => handleInvoiceFilterChange('userId', e.target.value)}
                        >
                          <option value="">Все пользователи</option>
                          {usersList.map(u => (
                            <option key={u.id} value={u.id}>
                              {u.login} ({u.email || 'без email'})
                            </option>
                          ))}
                        </Form.Select>
                      </Col>
                      <Col md={2}>
                        <Form.Control
                          placeholder="ИНН"
                          value={invoiceFilters.inn}
                          onChange={(e) => handleInvoiceFilterChange('inn', e.target.value)}
                        />
                      </Col>
                      <Col md={3}>
                        <Form.Control
                          placeholder="Организация"
                          value={invoiceFilters.organization}
                          onChange={(e) => handleInvoiceFilterChange('organization', e.target.value)}
                        />
                      </Col>
                      <Col md={2}>
                        <Form.Control
                          type="date"
                          value={invoiceFilters.startDate}
                          onChange={(e) => handleInvoiceFilterChange('startDate', e.target.value)}
                        />
                      </Col>
                      <Col md={2}>
                        <Form.Control
                          type="date"
                          value={invoiceFilters.endDate}
                          onChange={(e) => handleInvoiceFilterChange('endDate', e.target.value)}
                        />
                      </Col>
                    </Row>
                    <Row className="g-2 mt-2">
                      <Col className="text-end">
                        <Button
                          variant="outline-secondary"
                          className="me-2"
                          onClick={() => {
                            setInvoiceFilters({
                              userId: '',
                              inn: '',
                              organization: '',
                              startDate: '',
                              endDate: '',
                            });
                          }}
                        >
                          <FilterOutlined /> Сброс
                        </Button>
                        <Button variant="outline-primary" onClick={loadInvoiceHistory} disabled={loading}>
                          <ReloadOutlined /> Обновить
                        </Button>
                      </Col>
                    </Row>
                  </div>

                  {loading && activeTab === 'history' && historySubTab === 'invoices' ? (
                    <div className="text-center py-5">
                      <Spinner animation="border" variant="primary" />
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <Table striped bordered hover size="sm">
                          <thead>
                            <tr>
                              <th>Номер</th>
                              <th>Дата</th>
                              <th>Пользователь</th>
                              <th>ИНН</th>
                              <th>Организация</th>
                              <th>Тариф</th>
                              <th>Сумма</th>
                              <th>Действия</th>
                            </tr>
                          </thead>
                          <tbody>
                            {invoiceHistory.map((item) => (
                              <tr key={item.id}>
                                <td>{item.invoiceNumber}</td>
                                <td>{formatDate(item.issuedAt || item.createdAt)}</td>
                                <td>{item.user?.login || '—'}</td>
                                <td>{item.inn}</td>
                                <td>{item.organizationName}</td>
                                <td>{item.tariffName || item.tariff?.name || '—'}</td>
                                <td>{formatMoney(item.amount, item.currency)}</td>
                                <td>
                                  <div className="d-flex gap-1 align-items-center">
                                    {item.paidAt ? (
                                      <Badge bg="success">Зачислено</Badge>
                                    ) : (
                                      <Button
                                        variant="outline-success"
                                        size="sm"
                                        onClick={() => handleCreditInvoice(item)}
                                        disabled={
                                          invoiceCreditLoading === item.id
                                          || invoiceDownloadLoading === item.id
                                          || invoiceDeleteLoading === item.id
                                        }
                                      >
                                        {invoiceCreditLoading === item.id ? (
                                          <Spinner animation="border" size="sm" />
                                        ) : (
                                          <>
                                            <CheckCircleOutlined /> Зачислить
                                          </>
                                        )}
                                      </Button>
                                    )}
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      onClick={() => handleDownloadInvoice(item)}
                                      disabled={
                                        invoiceDownloadLoading === item.id
                                        || invoiceDeleteLoading === item.id
                                        || invoiceCreditLoading === item.id
                                      }
                                    >
                                      {invoiceDownloadLoading === item.id ? (
                                        <Spinner animation="border" size="sm" />
                                      ) : (
                                        <>
                                          <DownloadOutlined /> PDF
                                        </>
                                      )}
                                    </Button>
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() => handleDeleteInvoice(item)}
                                      disabled={
                                        invoiceDeleteLoading === item.id
                                        || invoiceDownloadLoading === item.id
                                        || invoiceCreditLoading === item.id
                                      }
                                    >
                                      {invoiceDeleteLoading === item.id ? (
                                        <Spinner animation="border" size="sm" />
                                      ) : (
                                        <>
                                          <DeleteOutlined /> Удалить
                                        </>
                                      )}
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                            {invoiceHistory.length === 0 && (
                              <tr>
                                <td colSpan="8" className="text-center py-4 text-muted">
                                  Счета не найдены
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                      {renderHistoryPagination(invoicePagination, setInvoicePagination)}
                    </>
                  )}
                </Tab>

                <Tab eventKey="balance" title="Операции с балансом">
                  <div className="mb-3">
                    <Row className="g-2">
                      <Col md={2}>
                        <Form.Select
                          value={historyFilters.userId}
                          onChange={(e) => handleHistoryFilterChange('userId', e.target.value)}
                        >
                          <option value="">Все пользователи</option>
                          {usersList.map(u => (
                            <option key={u.id} value={u.id}>
                              {u.login} ({u.email || 'без email'})
                            </option>
                          ))}
                        </Form.Select>
                      </Col>
                      <Col md={2}>
                        <Form.Select
                          value={historyFilters.operationType}
                          onChange={(e) => handleHistoryFilterChange('operationType', e.target.value)}
                        >
                          <option value="">Все типы</option>
                          <option value="deposit">Пополнение</option>
                          <option value="withdrawal">Вывод</option>
                          <option value="payment">Списание</option>
                          <option value="refund">Возврат</option>
                          <option value="adjustment">Корректировка</option>
                        </Form.Select>
                      </Col>
                      <Col md={2}>
                        <Form.Select
                          value={historyFilters.sourceFilter}
                          onChange={(e) => handleHistoryFilterChange('sourceFilter', e.target.value)}
                        >
                          <option value="">Все источники</option>
                          <option value="payment">Только оплаченные пакеты</option>
                          <option value="excludeManual">Без ручных пополнений</option>
                        </Form.Select>
                      </Col>
                      <Col md={2}>
                        <Form.Control
                          type="date"
                          value={historyFilters.startDate}
                          onChange={(e) => handleHistoryFilterChange('startDate', e.target.value)}
                        />
                      </Col>
                      <Col md={2}>
                        <Form.Control
                          type="date"
                          value={historyFilters.endDate}
                          onChange={(e) => handleHistoryFilterChange('endDate', e.target.value)}
                        />
                      </Col>
                      <Col md={2} className="text-end">
                        <Button
                          variant="outline-secondary"
                          onClick={() => {
                            setHistoryFilters({
                              userId: '',
                              operationType: '',
                              sourceFilter: '',
                              startDate: '',
                              endDate: '',
                            });
                          }}
                          className="me-2"
                        >
                          <FilterOutlined /> Сброс
                        </Button>
                        <Button variant="outline-primary" onClick={loadHistory} disabled={loading}>
                          <ReloadOutlined /> Обновить
                        </Button>
                      </Col>
                    </Row>
                  </div>

                  {loading && activeTab === 'history' && historySubTab === 'balance' ? (
                    <div className="text-center py-5">
                      <Spinner animation="border" variant="primary" />
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <Table striped bordered hover size="sm">
                          <thead>
                            <tr>
                              <th>Пользователь</th>
                              <th>Тип</th>
                              <th>Сумма (запросы)</th>
                              <th>Баланс до/после</th>
                              <th>Описание</th>
                              <th>Дата</th>
                            </tr>
                          </thead>
                          <tbody>
                            {balanceHistory.map((item) => {
                              const operation = item.operationType || item.operation_type || item.type;
                              const balanceBefore = item.balanceBefore ?? item.balance_before;
                              const balanceAfter = item.balanceAfter ?? item.balance_after;
                              const created = item.createdAt || item.created_at;
                              const description = formatBalanceHistoryDescription(item);

                              return (
                                <tr key={item.id}>
                                  <td>{item.user?.login || item.userId || '—'}</td>
                                  <td>{getOperationBadge(operation)}</td>
                                  <td>
                                    <span className={item.amount > 0 ? 'text-success' : 'text-danger'}>
                                      {item.amount > 0 ? '+' : ''}{item.amount}
                                    </span>
                                  </td>
                                  <td>
                                    {balanceBefore !== undefined && balanceAfter !== undefined ? (
                                      <small className="text-muted">
                                        {balanceBefore} → {balanceAfter}
                                      </small>
                                    ) : '—'}
                                  </td>
                                  <td>{description || '—'}</td>
                                  <td>{formatDate(created)}</td>
                                </tr>
                              );
                            })}
                            {balanceHistory.length === 0 && (
                              <tr>
                                <td colSpan="6" className="text-center py-4 text-muted">
                                  История пуста
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                      {renderHistoryPagination(historyPagination, setHistoryPagination)}
                    </>
                  )}
                </Tab>
              </Tabs>
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>

      {/* Модальное окно изменения баланса */}
      <Modal show={showAddBalanceModal} onHide={() => setShowAddBalanceModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Изменить баланс: {selectedUser?.login}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAddBalanceSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Тип операции</Form.Label>
              <div>
                <Form.Check
                  inline
                  type="radio"
                  label="Пополнить (+)"
                  name="balanceOperation"
                  id="balanceOpAdd"
                  checked={balanceForm.operation === 'add'}
                  onChange={() => setBalanceForm({ ...balanceForm, operation: 'add' })}
                  className="text-success fw-bold"
                />
                <Form.Check
                  inline
                  type="radio"
                  label="Списать (-)"
                  name="balanceOperation"
                  id="balanceOpDeduct"
                  checked={balanceForm.operation === 'deduct'}
                  onChange={() => setBalanceForm({ ...balanceForm, operation: 'deduct' })}
                  className="text-danger fw-bold"
                />
              </div>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Количество запросов</Form.Label>
              <Form.Control
                type="number"
                min="1"
                step="1"
                value={balanceForm.amount}
                onChange={(e) => setBalanceForm({ ...balanceForm, amount: e.target.value })}
                placeholder="100"
                required
              />
              <Form.Text className="text-muted">
                {balanceForm.operation === 'deduct' 
                  ? `Будет списано ${balanceForm.amount || 0} запросов. Текущий баланс: ${selectedUser?.requestsBalance || selectedUser?.requests_balance || 0}`
                  : `Будет начислено ${balanceForm.amount || 0} запросов.`
                }
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Причина</Form.Label>
              <Form.Control
                type="text"
                value={balanceForm.reason}
                onChange={(e) => setBalanceForm({ ...balanceForm, reason: e.target.value })}
                placeholder={balanceForm.operation === 'add' ? "Бонус" : "Корректировка"}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddBalanceModal(false)}>
              Отмена
            </Button>
            <Button 
              variant={balanceForm.operation === 'add' ? 'success' : 'danger'} 
              type="submit"
            >
              {balanceForm.operation === 'add' ? 'Пополнить' : 'Списать'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Модальное окно результата пересчёта зависших балансов */}
      <Modal
        show={showCreditResultModal}
        onHide={() => { setShowCreditResultModal(false); setCreditResult(null); }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Пересчёт зависших балансов</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {creditResult && (
            <>
              <Alert variant="info" className="mb-3">
                {creditResult.message}
              </Alert>
              {(creditResult.credited > 0 || creditResult.failed > 0) && (
                <p className="text-muted small mb-2">
                  Начислено: {creditResult.credited}, с ошибкой: {creditResult.failed}
                  {creditResult.total != null && `, всего найдено: ${creditResult.total}`}
                </p>
              )}
              {creditResult.results && creditResult.results.length > 0 && (
                <div className="table-responsive">
                  <Table striped bordered hover size="sm">
                    <thead>
                      <tr>
                        <th>Платёж</th>
                        <th>Пользователь</th>
                        <th>Логин</th>
                        <th>Запросов</th>
                        <th>Новый баланс</th>
                        <th>Статус</th>
                      </tr>
                    </thead>
                    <tbody>
                      {creditResult.results.map((row, idx) => (
                        <tr key={row.paymentId + '-' + idx}>
                          <td>{row.paymentId}</td>
                          <td>{row.userId}</td>
                          <td>{row.login ?? '—'}</td>
                          <td>{row.requestsCount ?? '—'}</td>
                          <td>{row.newBalance != null ? row.newBalance : '—'}</td>
                          <td>
                            {row.status === 'credited' ? (
                              <Badge bg="success">Начислено</Badge>
                            ) : (
                              <span className="text-danger small">{row.error || row.status}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowCreditResultModal(false); setCreditResult(null); }}>
            Закрыть
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Модальное окно тарифа */}
      <Modal show={showTariffModal} onHide={() => setShowTariffModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{editingTariff ? 'Редактировать тариф' : 'Создать тариф'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Название</Form.Label>
              <Form.Control
                type="text"
                value={tariffForm.name}
                onChange={(e) => setTariffForm({ ...tariffForm, name: e.target.value })}
                placeholder="Базовый тариф"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Количество запросов</Form.Label>
              <Form.Control
                type="number"
                step="1"
                value={tariffForm.requests_count}
                onChange={(e) => setTariffForm({ ...tariffForm, requests_count: e.target.value })}
                placeholder="100"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Цена (₽)</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                value={tariffForm.price}
                onChange={(e) => setTariffForm({ ...tariffForm, price: e.target.value })}
                placeholder="10.00"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Описание</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={tariffForm.description}
                onChange={(e) => setTariffForm({ ...tariffForm, description: e.target.value })}
                placeholder="Описание тарифа"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="tariffIsActive"
                label="Тариф активен"
                checked={tariffForm.is_active}
                onChange={(e) => setTariffForm({ ...tariffForm, is_active: e.target.checked })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="tariffIsPromotional"
                label="Акционный (один раз на пользователя)"
                checked={tariffForm.is_promotional}
                onChange={(e) => setTariffForm({ ...tariffForm, is_promotional: e.target.checked })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="tariffIsFirstPurchase"
                label="Только для первой покупки"
                checked={tariffForm.is_first_purchase_only}
                onChange={(e) => setTariffForm({ ...tariffForm, is_first_purchase_only: e.target.checked })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="tariffIsPersonal"
                label="Персональный тариф"
                checked={tariffForm.is_personal}
                onChange={(e) => setTariffForm({
                  ...tariffForm,
                  is_personal: e.target.checked,
                  eligibleUserIds: e.target.checked ? tariffForm.eligibleUserIds : [],
                })}
              />
            </Form.Group>
            {tariffForm.is_personal && (
              <Form.Group className="mb-3">
                <Form.Label>Пользователи с доступом</Form.Label>
                <Select
                  mode="multiple"
                  showSearch
                  filterOption={false}
                  placeholder="Поиск по логину, email или телефону"
                  value={tariffForm.eligibleUserIds}
                  loading={tariffUserSearchLoading}
                  onSearch={searchTariffUsers}
                  onFocus={() => searchTariffUsers('')}
                  onChange={(eligibleUserIds) => setTariffForm({ ...tariffForm, eligibleUserIds })}
                  options={tariffUserOptions.map((user) => ({
                    value: user.id,
                    label: formatUserOptionLabel(user),
                  }))}
                  style={{ width: '100%' }}
                />
                <Form.Text className="text-muted">
                  Выберите одного или нескольких зарегистрированных пользователей
                </Form.Text>
              </Form.Group>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Переход с другого сайта (type)</Form.Label>
              <Form.Control
                type="number"
                min="1"
                step="1"
                placeholder="Например: 6"
                value={tariffForm.landing_type}
                onChange={(e) => setTariffForm({ ...tariffForm, landing_type: e.target.value })}
              />
              <Form.Text className="text-muted d-block">
                Уникальный номер для ссылки вида <code>/confirm?type=N</code>.
                Если оставить пустым — будет использоваться <code>/confirm?tariff=&lt;uuid&gt;</code>.
              </Form.Text>
              <div className="mt-2">
                <code className="small">
                  {getTariffLandingPath(buildTariffPreviewFromForm(tariffForm, editingTariff?.id))}
                </code>
                {(tariffForm.landing_type !== '' || editingTariff?.id) && (
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 ms-2"
                    onClick={() => handleCopyTariffLink(
                      buildTariffPreviewFromForm(tariffForm, editingTariff?.id)
                    )}
                  >
                    Копировать
                  </Button>
                )}
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTariffModal(false)}>
            Отмена
          </Button>
          <Button variant="primary" onClick={handleSaveTariff}>
            Сохранить
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default BalanceManagement;
